import React, { useState, createElement } from 'react';
import { useUser } from '../../context/UserContext';
import { useShift } from '../../context/ShiftContext';
import { DownloadIcon, UploadIcon } from 'lucide-react';
const DataManagement = () => {
  const {
    users,
    user: currentUser
  } = useUser();
  const {
    shifts,
    requests,
    trades,
    holidays,
    addActivityLog
  } = useShift();
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const handleExportData = () => {
    const data = {
      users: localStorage.getItem('apnUsers'),
      shifts: localStorage.getItem('apnShifts'),
      requests: localStorage.getItem('apnRequests'),
      trades: localStorage.getItem('apnTrades'),
      holidays: localStorage.getItem('apnHolidays'),
      exportDate: new Date().toISOString()
    };
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rowdate-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    addActivityLog({
      userId: currentUser?.id,
      userName: currentUser?.name,
      action: 'Data Export',
      details: 'Exported all application data as backup file',
      category: 'data'
    });
    setSuccessMessage('Data exported successfully!');
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };
  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);
        // Validate the data structure
        if (!data.users || !data.shifts || !data.holidays) {
          setError('Invalid data file format');
          return;
        }
        // Restore all data
        if (data.users) localStorage.setItem('apnUsers', data.users);
        if (data.shifts) localStorage.setItem('apnShifts', data.shifts);
        if (data.requests) localStorage.setItem('apnRequests', data.requests);
        if (data.trades) localStorage.setItem('apnTrades', data.trades);
        if (data.holidays) localStorage.setItem('apnHolidays', data.holidays);
        addActivityLog({
          userId: currentUser?.id,
          userName: currentUser?.name,
          action: 'Data Import',
          details: 'Imported application data from backup file',
          category: 'data'
        });
        setSuccessMessage('Data imported successfully! Please refresh the page to see the changes.');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (err) {
        setError('Error parsing data file. Please ensure it is a valid JSON file.');
      }
    };
    reader.readAsText(file);
  };
  return <div data-id="element-123">
      <h3 className="text-xl font-semibold mb-6 text-gray-700" data-id="element-124">
        Data Management
      </h3>
      <p className="text-gray-600 mb-6" data-id="element-125">
        Export all application data for backup or import previously exported
        data to restore the system.
      </p>
      {successMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" data-id="element-126">
          {successMessage}
        </div>}
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" data-id="element-127">
          {error}
        </div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-128">
        <div className="bg-white shadow rounded-lg p-6" data-id="element-129">
          <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-130">
            Export Data
          </h4>
          <p className="text-sm text-gray-600 mb-4" data-id="element-131">
            Download a backup file containing all users, shifts, requests,
            trades, and holidays.
          </p>
          <button onClick={handleExportData} className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-132">
            <DownloadIcon size={16} className="mr-2" data-id="element-133" />
            Export All Data
          </button>
          <div className="mt-6 border-t pt-6" data-id="element-134">
            <h5 className="text-sm font-medium text-gray-900 mb-2" data-id="element-135">
              Current Data Summary
            </h5>
            <ul className="text-sm text-gray-600 space-y-1" data-id="element-136">
              <li data-id="element-137">Users: {users.length}</li>
              <li data-id="element-138">Shifts: {shifts.length}</li>
              <li data-id="element-139">Requests: {requests.length}</li>
              <li data-id="element-140">Trades: {trades.length}</li>
              <li data-id="element-141">Holidays: {holidays.length}</li>
            </ul>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6" data-id="element-142">
          <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-143">
            Import Data
          </h4>
          <p className="text-sm text-gray-600 mb-4" data-id="element-144">
            Upload a previously exported backup file to restore all data.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4" data-id="element-145">
            <p className="text-sm text-yellow-800" data-id="element-146">
              <strong data-id="element-147">Warning:</strong> Importing data will overwrite all
              current data in the system. This action cannot be undone.
            </p>
          </div>
          <label className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer" data-id="element-148">
            <UploadIcon size={16} className="mr-2" data-id="element-149" />
            Select Backup File
            <input type="file" accept=".json" onChange={handleImportData} className="hidden" data-id="element-150" />
          </label>
        </div>
      </div>
    </div>;
};
export default DataManagement;